import {
  AIManagementPanel,
  AIManagementPanelButton,
} from "@/components/ai-management-panel";
import { CompanyPermissions } from "@/app/api/types/superadmin";
import { useUserOptional } from "@/components/auth-provider";
import { CompanyCheck } from "@/components/company-check";
import { GlobalBootScreen } from "@/components/GlobalBootScreen";
import { GuideOverlay } from "@/components/guide/GuideOverlay";
import { GuideDevLauncher } from "@/components/guide/GuideDevLauncher";
import { Header } from "@/components/header";
import { PermissionCheck } from "@/components/permission-check";
import { AppSidebar } from "@/components/sidebar";
import { PriceEmptyRenderProbe } from "@/components/PriceEmptyRenderProbe";
import { usePathname } from "@/hooks/use-pathname-compat";
import { usesMobileApp } from "@/lib/mobile-app";
import { offlineTelemetrySink } from "@/lib/offline-telemetry";
import { isPublicRoute } from "@/lib/public-routes";
import { AISyncProvider } from "@stockline/web";
import React from "react";

// Install prompt for the website/PWA (web target = the installable PWA). Lazy +
// guarded so the dynamic import is dead-code-eliminated out of the capacitor
// build (the native app isn't installed via a browser prompt).
const PWAInstallPrompt = React.lazy(() =>
  import("@mobile/components/PWAInstallPrompt").then((m) => ({
    default: m.PWAInstallPrompt,
  })),
);

export function PageContent({ children }: { children: React.ReactNode }) {
  const pathname = usePathname();
  const isLandingPage = pathname === "/";
  // Both superadmin surfaces are internal, superadmin-only tools that run
  // without a company (`company_id = NULL`). They own their own chrome
  // (`/superadmin/layout`, `/ai-superadmin/layout`) and must bypass the
  // company-scoped dashboard shell — the `GlobalBootScreen` SyncEngine pull
  // (which never resolves with no company) and the `PermissionCheck`
  // company-permission gate. `/ai-superadmin` doesn't match the `/superadmin`
  // prefix, so list it explicitly.
  const isSuperAdminPage =
    pathname?.startsWith("/superadmin") ||
    pathname?.startsWith("/ai-superadmin");

  // Identity-based counterpart to the route-prefix bypass above. A platform
  // super admin has no company (`company_id = NULL`), so the company-scoped
  // SyncEngine pull behind `GlobalBootScreen` 401s on every resource and its
  // critical wave never becomes ready — the boot screen would hang at 0%
  // forever. The route-prefix bypass only covers `/superadmin*` and
  // `/ai-superadmin*`; a company-less super admin who lands on any other route
  // (e.g. the post-login redirect to `/home`) would still hang. Bypass the
  // dashboard shell for them regardless of route. A super admin that DOES
  // belong to a company keeps the normal shell — they have data to sync.
  const user = useUserOptional();
  const isCompanylessSuperAdmin =
    user?.company_permissions === CompanyPermissions.SUPER_ADMIN &&
    !user.company_id;

  // The floating AI assistant is global on the web dashboard, except in the
  // lean mobile app (its bottom nav owns AI access) where it isn't mounted at
  // all. On the chat-owning routes — the full AI page (the page itself IS the
  // chat) and the BI/steering pages (their own docked chat) — the card stays
  // MOUNTED but suppressed: unmounting it would reset its chat (selected
  // conversation, unsent text, resize) every time you pass through those
  // routes, so we hide it there instead and drop only the launcher trigger.
  const isMobile = usesMobileApp();
  const onChatOwningRoute =
    pathname === "/ai" || (pathname?.startsWith("/steering") ?? false);

  if (
    isLandingPage ||
    isPublicRoute(pathname) ||
    isSuperAdminPage ||
    isCompanylessSuperAdmin
  ) {
    return <>{children}</>;
  }

  // Single dashboard tree. Header decides per-route whether to render the
  // page-title chrome or return children raw (recap routes + detail
  // pages opt out — they own their own header). Structurally consistent
  // across every dashboard route, so React Router's nested-route layouts
  // (e.g. RecapWorkspaceLayout with the chat panel) persist across child
  // route changes — without this, navigating between two routes that
  // used to take different PageContent branches would reconcile to a
  // different tree shape under <AppSidebar> and unmount everything below.
  return (
    <AISyncProvider overrides={{ telemetry: offlineTelemetrySink }}>
      <GlobalBootScreen />
      <PriceEmptyRenderProbe />
      <AppSidebar>
        <Header>
          <PermissionCheck>
            <CompanyCheck>
              {children}
              {!isMobile && (
                <>
                  <AIManagementPanel suppressed={onChatOwningRoute} />
                  {!onChatOwningRoute && <AIManagementPanelButton />}
                  <GuideOverlay />
                  <GuideDevLauncher />
                </>
              )}
            </CompanyCheck>
          </PermissionCheck>
        </Header>
      </AppSidebar>
      {__TARGET__ !== "capacitor" && (
        <React.Suspense fallback={null}>
          <PWAInstallPrompt />
        </React.Suspense>
      )}
    </AISyncProvider>
  );
}
